import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { APPS_REGISTRY } from './apps-registry';
import styles from './AppLauncher.module.css';
/**
 * macOS-style glassmorphic app launcher
 * Features:
 * - Frosted glass effect with backdrop-filter
 * - Smooth scroll with lazy movement (parallax delay)
 * - GPU-accelerated animations
 * - Responsive behavior (desktop/tablet/mobile)
 * - Keyboard navigation support
 * - WCAG AA accessible
 */
export function AppLauncher({ isAuthenticated = false, currentApp, position = 'left', enableParallax = true, className = '', }) {
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(true); // Collapsed by default
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const launcherRef = useRef(null);
    const animationFrameRef = useRef(undefined);
    const currentScrollRef = useRef(0);
    const targetScrollRef = useRef(0);
    const collapseTimerRef = useRef(undefined);
    // Filter apps based on authentication and exclude IntranetSpider (it has its own app tiles)
    const visibleApps = (isAuthenticated
        ? APPS_REGISTRY // Show all apps when authenticated
        : APPS_REGISTRY.filter(app => !app.requiresAuth) // Only public apps when not authenticated
    ).filter(app => app.id !== 'intranet'); // Exclude IntranetSpider
    // Smooth scroll with parallax effect (lazy movement)
    useEffect(() => {
        if (!enableParallax)
            return;
        const handleScroll = () => {
            targetScrollRef.current = window.scrollY;
        };
        const smoothScroll = () => {
            // Parallax factor: sidebar moves slower than page scroll (0.85 = 15% slower)
            const parallaxFactor = 0.85;
            const targetY = targetScrollRef.current * parallaxFactor;
            // Smooth interpolation (lerp) with 0.08 factor for natural feel
            const lerpFactor = 0.08;
            currentScrollRef.current += (targetY - currentScrollRef.current) * lerpFactor;
            setScrollPosition(currentScrollRef.current);
            animationFrameRef.current = requestAnimationFrame(smoothScroll);
        };
        // Start smooth scroll loop
        animationFrameRef.current = requestAnimationFrame(smoothScroll);
        // Add scroll listener with passive flag for better performance
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (animationFrameRef.current !== undefined) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [enableParallax]);
    // Keyboard navigation
    const handleKeyDown = (e, app) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            window.location.href = app.url;
        }
    };
    // Auto-expand on mouseover, collapse after 3 seconds
    const handleMouseEnter = () => {
        // Clear any pending collapse timer
        if (collapseTimerRef.current) {
            clearTimeout(collapseTimerRef.current);
        }
        setIsCollapsed(false);
    };
    const handleMouseLeave = () => {
        // Start 3-second timer to collapse
        collapseTimerRef.current = setTimeout(() => {
            setIsCollapsed(true);
        }, 3000);
    };
    // Drag to move functionality
    const handleMouseDown = (e) => {
        // Only drag if clicking on the container background (not on buttons or app tiles)
        const target = e.target;
        if (target.closest('button') || target.closest('a[role="listitem"]'))
            return;
        setIsDragging(true);
        if (launcherRef.current) {
            const rect = launcherRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            });
        }
    };
    useEffect(() => {
        if (!isDragging)
            return;
        const handleMouseMove = (e) => {
            if (launcherRef.current) {
                const newX = e.clientX - dragOffset.x;
                const newY = e.clientY - dragOffset.y;
                launcherRef.current.style.left = `${newX}px`;
                launcherRef.current.style.top = `${newY}px`;
                launcherRef.current.style.transform = 'none'; // Disable parallax while dragging
            }
        };
        const handleMouseUp = () => {
            setIsDragging(false);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);
    // Clean up collapse timer on unmount
    useEffect(() => {
        return () => {
            if (collapseTimerRef.current) {
                clearTimeout(collapseTimerRef.current);
            }
        };
    }, []);
    // Calculate transform for parallax
    const parallaxTransform = enableParallax
        ? `translateY(calc(-50% + ${scrollPosition * 0.15}px))`
        : 'translateY(-50%)';
    return (_jsx("nav", { ref: launcherRef, className: `${styles.launcher} ${styles[position]} ${isCollapsed ? styles.collapsed : styles.expanded} ${isDragging ? styles.dragging : ''} ${className}`, style: {
            transform: isDragging ? 'none' : parallaxTransform,
            cursor: isDragging ? 'grabbing' : 'default',
        }, role: "navigation", "aria-label": "App Launcher", onMouseDown: handleMouseDown, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: _jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.hitbar, children: _jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "12", cy: "8", r: "2.5", fill: "currentColor", opacity: "0.4" }), _jsx("circle", { cx: "12", cy: "12", r: "2.5", fill: "currentColor", opacity: "0.4" }), _jsx("circle", { cx: "12", cy: "16", r: "2.5", fill: "currentColor", opacity: "0.4" })] }) }), !isCollapsed && (_jsx("div", { className: styles.appGrid, role: "list", children: visibleApps.map((app) => {
                        const isActive = app.id === currentApp;
                        return (_jsxs("a", { href: app.url, className: `${styles.appTile} ${isActive ? styles.active : ''}`, style: {
                                // @ts-expect-error - CSS custom property
                                '--app-color': app.color,
                            }, role: "listitem", "aria-label": `Launch ${app.name}`, "aria-current": isActive ? 'page' : undefined, tabIndex: 0, onKeyDown: (e) => handleKeyDown(e, app), title: `${app.name} - ${app.description}`, children: [_jsxs("div", { className: styles.iconWrapper, children: [_jsx("img", { src: app.icon, alt: app.name, className: styles.icon, loading: "lazy" }), isActive && _jsx("div", { className: styles.activeIndicator })] }), _jsxs("span", { className: styles.tooltip, children: [_jsx("strong", { children: app.name }), _jsx("span", { className: styles.tooltipDescription, children: app.description })] })] }, app.id));
                    }) }))] }) }));
}
export function AppTile({ app, isActive = false, onNavigate }) {
    const handleClick = (e) => {
        if (onNavigate) {
            e.preventDefault();
            onNavigate(app.url);
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (onNavigate) {
                onNavigate(app.url);
            }
            else {
                window.location.href = app.url;
            }
        }
    };
    return (_jsxs("a", { href: app.url, className: `${styles.appTile} ${isActive ? styles.active : ''}`, style: {
            // @ts-expect-error - CSS custom property
            '--app-color': app.color,
        }, onClick: handleClick, onKeyDown: handleKeyDown, role: "listitem", "aria-label": `Launch ${app.name}`, "aria-current": isActive ? 'page' : undefined, tabIndex: 0, children: [_jsxs("div", { className: styles.iconWrapper, children: [_jsx("img", { src: app.icon, alt: "", className: styles.icon, loading: "lazy" }), isActive && _jsx("div", { className: styles.activeIndicator })] }), _jsx("span", { className: styles.appName, children: app.name })] }));
}
